import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import Header from "../components/user/Header";
import Footer from "../components/user/Footer";
import FloatingActions from "../components/user/FloatingActions";



export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    eventType: "",
    eventDate: "",
    message: "",
  });

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.eventType) {
      newErrors.eventType = "Please select an event type";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setFormStatus({ submitting: true, success: false, error: null });

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));

      setFormStatus({ submitting: false, success: true, error: null });
      setFormData({
        name: "",
        email: "",
        phone: "",
        eventType: "",
        eventDate: "",
        message: "",
      });

      setTimeout(() => {
        setFormStatus({ submitting: false, success: false, error: null });
      }, 5000);
    } catch (error) {
      console.error("Form submission error:", error);
      setFormStatus({
        submitting: false,
        success: false,
        error: "Failed to send message. Please try again.",
      });
    }
  };

  const eventTypes = [
    "Wedding",
    "Corporate Event",
    "Birthday Party",
    "Anniversary",
    "Conference",
    "Gala",
    "Other",
  ];

  const contactInfo = [
    {
      icon: <MapPin size={24} />,
      title: "Visit Us",
      content: "8 shaki crescent St, Surulere, Lagos 101241, Lagos, Nigeria",
      link: "https://maps.app.goo.gl/m9iAyeNt89fG56u46",
      linkText: "Get Directions",
    },
    {
      icon: <Phone size={24} />,
      title: "Call Us",
      content: "+234 (0)802 346 1907",
      link: "tel:+23408023461907",
      linkText: "Call Now",
    },
    {
      icon: <Mail size={24} />,
      title: "Email Us",
      content: "info@ststephensrentals.com",
      link: "mailto:info@ststephensrentals.com",
      linkText: "Send Email",
    },
  ];

  const businessHours = [
    { day: "Monday - Friday", hours: "9:00 AM - 6:00 PM" },
    { day: "Saturday", hours: "9:00 AM - 5:00 PM" },
    { day: "Sunday", hours: "Closed" },
  ];

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="relative h-[300px] md:h-[400px] overflow-hidden bg-gradient-to-br from-[#62397f] via-[#7C3AED] to-[#6D28D9]">
        <div className="absolute inset-0 opacity-10 bg-cover bg-center" style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=2000&h=800&fit=crop")' }}></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
        <div className="relative h-full max-w-[1400px] mx-auto px-6 flex items-center justify-center text-center">
          <div className="w-full">
            <h1
              className="text-white font-medium font-serif leading-tight mb-6"
              style={{
                fontSize: "clamp(36px, 6vw, 64px)",
              }}
            >
              Contact Us for Rentals
            </h1>
            <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              Need premium equipment for your next event? Reach out to our team to discuss your rental needs, setup requirements, and event vision.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section
        className="py-20 md:py-28 px-6 bg-white dark:bg-[#121212]"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Form */}
            <div>
              <div className="mb-8">
                <h2
                  className="text-black dark:text-white font-medium font-serif leading-tight mb-3"
                  style={{
                    fontSize: "clamp(28px, 4vw, 36px)",
                  }}
                >
                  Send Us a Message
                </h2>
                <p className="text-[#666666] dark:text-[#A0A0A0] text-base md:text-lg">
                  Fill out the form below and we'll get back to you within 24
                  hours.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-semibold text-[#333333] dark:text-[#E0E0E0] mb-2"
                  >
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border ${errors.name
                      ? "border-red-500 focus:border-red-500"
                      : "border-[#E9E9E9] dark:border-[#333333] focus:border-[#62397f] dark:focus:border-[#A78BFA]"
                      } bg-white dark:bg-[#1E1E1E] text-black dark:text-white placeholder-[#999999] dark:placeholder-[#666666] outline-none transition-all duration-200 focus:ring-2 focus:ring-[#62397f]/20`}
                    placeholder="John Doe"
                  />
                  {errors.name && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-[#333333] dark:text-[#E0E0E0] mb-2"
                  >
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border ${errors.email
                      ? "border-red-500 focus:border-red-500"
                      : "border-[#E9E9E9] dark:border-[#333333] focus:border-[#62397f] dark:focus:border-[#A78BFA]"
                      } bg-white dark:bg-[#1E1E1E] text-black dark:text-white placeholder-[#999999] dark:placeholder-[#666666] outline-none transition-all duration-200 focus:ring-2 focus:ring-[#62397f]/20`}
                    placeholder="john@example.com"
                  />
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-semibold text-[#333333] dark:text-[#E0E0E0] mb-2"
                  >
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border ${errors.phone
                      ? "border-red-500 focus:border-red-500"
                      : "border-[#E9E9E9] dark:border-[#333333] focus:border-[#62397f] dark:focus:border-[#A78BFA]"
                      } bg-white dark:bg-[#1E1E1E] text-black dark:text-white placeholder-[#999999] dark:placeholder-[#666666] outline-none transition-all duration-200 focus:ring-2 focus:ring-[#62397f]/20`}
                    placeholder="+234 (0)802 346 1907"
                  />
                  {errors.phone && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="eventType"
                    className="block text-sm font-semibold text-[#333333] dark:text-[#E0E0E0] mb-2"
                  >
                    Event Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="eventType"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border ${errors.eventType
                      ? "border-red-500 focus:border-red-500"
                      : "border-[#E9E9E9] dark:border-[#333333] focus:border-[#62397f] dark:focus:border-[#A78BFA]"
                      } bg-white dark:bg-[#1E1E1E] text-black dark:text-white outline-none transition-all duration-200 focus:ring-2 focus:ring-[#62397f]/20`}
                  >
                    <option value="">Select event type</option>
                    {eventTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {errors.eventType && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.eventType}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="eventDate"
                    className="block text-sm font-semibold text-[#333333] dark:text-[#E0E0E0] mb-2"
                  >
                    Event Date (Optional)
                  </label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-[#E9E9E9] dark:border-[#333333] focus:border-[#62397f] dark:focus:border-[#A78BFA] bg-white dark:bg-[#1E1E1E] text-black dark:text-white outline-none transition-all duration-200 focus:ring-2 focus:ring-[#62397f]/20"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-semibold text-[#333333] dark:text-[#E0E0E0] mb-2"
                  >
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border ${errors.message
                      ? "border-red-500 focus:border-red-500"
                      : "border-[#E9E9E9] dark:border-[#333333] focus:border-[#62397f] dark:focus:border-[#A78BFA]"
                      } bg-white dark:bg-[#1E1E1E] text-black dark:text-white placeholder-[#999999] dark:placeholder-[#666666] outline-none transition-all duration-200 focus:ring-2 focus:ring-[#62397f]/20 resize-none`}
                    placeholder="Tell us about your event, rental needs, and any specific requirements..."
                  ></textarea>
                  {errors.message && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.message}
                    </p>
                  )}
                </div>

                {formStatus.success && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl flex items-start gap-3">
                    <CheckCircle2
                      size={20}
                      className="text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
                    />
                    <div>
                      <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                        Message sent successfully!
                      </p>
                      <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                        We'll get back to you within 24 hours.
                      </p>
                    </div>
                  </div>
                )}

                {formStatus.error && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3">
                    <AlertCircle
                      size={20}
                      className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                    />
                    <p className="text-sm text-red-800 dark:text-red-300">
                      {formStatus.error}
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus.submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#62397f] hover:bg-[#7C3AED] text-white font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_8px_24px_rgba(139,92,246,0.25)] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {formStatus.submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Sending Message...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    className="bg-[#FAFAFA] dark:bg-[#1E1E1E] rounded-2xl p-6 border border-[#E9E9E9] dark:border-[#333333] transition-all duration-300 hover:border-[#62397f] dark:hover:border-[#A78BFA] hover:shadow-[0_8px_24px_rgba(139,92,246,0.12)] dark:hover:shadow-[0_8px_24px_rgba(0,0,0,0.3)]"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#62397f] to-[#7C3AED] rounded-xl flex items-center justify-center text-white flex-shrink-0">
                        {info.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-black dark:text-white mb-2">
                          {info.title}
                        </h3>
                        <p className="text-[#666666] dark:text-[#A0A0A0] mb-3 leading-relaxed">
                          {info.content}
                        </p>
                        <Link
                          to={info.link}
                          target={
                            info.link.startsWith("http") ? "_blank" : undefined
                          }
                          rel={
                            info.link.startsWith("http")
                              ? "noopener noreferrer"
                              : undefined
                          }
                          className="inline-flex items-center gap-2 text-[#62397f] dark:text-[#A78BFA] font-semibold text-sm hover:gap-3 transition-all duration-200"
                        >
                          {info.linkText}
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7.5 15L12.5 10L7.5 5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#FAFAFA] dark:bg-[#1E1E1E] rounded-2xl p-6 border border-[#E9E9E9] dark:border-[#333333]">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#62397f] to-[#7C3AED] rounded-xl flex items-center justify-center text-white">
                    <Clock size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-black dark:text-white">
                    Business Hours
                  </h3>
                </div>
                <div className="space-y-3 ml-15">
                  {businessHours.map((schedule, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <span className="text-[#666666] dark:text-[#A0A0A0]">
                        {schedule.day}
                      </span>
                      <span className="font-semibold text-black dark:text-white">
                        {schedule.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#FAFAFA] dark:bg-[#1E1E1E] rounded-2xl overflow-hidden border border-[#E9E9E9] dark:border-[#333333]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3963.952915004108!2d3.3594495!3d6.5113186!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x103b8c3a7a7a7a7a%3A0x7a7a7a7a7a7a7a7a!2s8%20Shaki%20Cres%2C%20Surulere%2C%20Lagos!5e0!3m2!1sen!2sng!4v1234567890123!5m2!1sen!2sng"
                  title="St. Stephen's Rentals Location Map - 8 Shaki Crescent, Surulere, Lagos"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </>
  );
}
